/**
 * PWA Update Prompt Component
 *
 * Automatically handles service worker updates in the background.
 * Shows a brief notification when updating and auto-refreshes.
 */

import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { FiRefreshCw, FiDownloadCloud } from 'react-icons/fi'
import { usePWA } from '../../hooks/usePWA'
import { useEffect, useState } from 'react'

const PWAUpdatePrompt = () => {
  const { t } = useTranslation()
  const { needRefresh, offlineReady, updateServiceWorker } = usePWA()
  const [isUpdating, setIsUpdating] = useState(false)
  const [showOfflineReady, setShowOfflineReady] = useState(false)

  // Auto-trigger update when available
  useEffect(() => {
    if (needRefresh && !isUpdating) {
      setIsUpdating(true)
      // Brief delay to show the notification before update
      setTimeout(() => {
        updateServiceWorker()
      }, 1500)
    }
  }, [needRefresh, isUpdating, updateServiceWorker])

  // Show offline ready notification briefly
  useEffect(() => {
    if (offlineReady) {
      setShowOfflineReady(true)
      const timer = setTimeout(() => setShowOfflineReady(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [offlineReady])

  // Don't show anything if nothing to display
  if (!isUpdating && !showOfflineReady) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50"
      >
        <div className="bg-white rounded-xl shadow-lg border border-mk-pink-200 overflow-hidden">
          <div className="bg-gradient-to-r from-mk-pink-500 to-mk-pink-600 px-4 py-3 flex items-center gap-3">
            {isUpdating ? (
              <>
                <FiRefreshCw className="w-5 h-5 text-white animate-spin" />
                <span className="text-white font-medium text-sm">
                  {t('pwa.updating')}
                </span>
              </>
            ) : (
              <>
                <FiDownloadCloud className="w-5 h-5 text-white" />
                <span className="text-white font-medium text-sm">
                  {t('pwa.offlineReady')}
                </span>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default PWAUpdatePrompt
